import { Head, Link } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { Submission } from "@/interfaces/submission";

// The code behind a site somebody pasted in, laid out to be read. For a kid
// with no GitHub this page is where their source lives, so it shows every
// line exactly as handed in and links the raw files beside it.
export default function SiteCode({
  submission,
  event,
  html,
  css,
  live_url,
}: {
  submission: Submission;
  event: SatelliteEvent | null;
  html: string;
  css: string | null;
  live_url: string | null;
}) {
  const base = `/code/${submission.public_slug}`;

  return (
    <>
      <Head title={`Code for ${submission.title} by ${submission.author}`} />

      <div className="bg-bg bg-paper min-h-dvh">
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8">
          <header className="flex flex-col gap-1">
            <h1 className="font-display text-heading text-ink leading-tight font-bold">
              {submission.title}
            </h1>
            <p className="text-body text-ink-muted">
              by {submission.author}, built at{" "}
              {event ? (
                <Link
                  href={`/events/${event.slug ?? event.id}/submissions`}
                  className="text-link underline"
                >
                  {event.name}
                </Link>
              ) : (
                "Boba Bash"
              )}
            </p>
            {live_url && (
              <a href={live_url} className="text-body text-link underline">
                See it live
              </a>
            )}
          </header>

          <CodeFile name="index.html" href={`${base}/index.html`} code={html} />
          {css && (
            <CodeFile name="style.css" href={`${base}/style.css`} code={css} />
          )}
        </div>
      </div>
    </>
  );
}

function CodeFile({
  name,
  href,
  code,
}: {
  name: string;
  href: string;
  code: string;
}) {
  const lines = code.split("\n");

  return (
    <Panel
      title={name}
      actions={
        <a href={href} className="text-caption text-link underline">
          raw
        </a>
      }
    >
      <pre className="text-caption overflow-x-auto bg-white p-4 font-mono leading-relaxed">
        <code>
          {lines.map((line, i) => (
            <div key={i} className="flex gap-4">
              <span className="text-ink-soft w-8 shrink-0 text-right select-none">
                {i + 1}
              </span>
              <span className="text-ink whitespace-pre">{line}</span>
            </div>
          ))}
        </code>
      </pre>
    </Panel>
  );
}
